// Created on 2022-02-10
// Created by Vitaut Tryputsin
//
// Copyright (c) 2022 OPEN CASCADE SAS
//
// This file is part of commercial software by OPEN CASCADE SAS,
// furnished in accordance with the terms and conditions of the contract
// and with the inclusion of this copyright notice.
// This file or any part thereof may not be provided or otherwise
// made available to any third party.
//
// No ownership title to the software is transferred hereby.
//
// OPEN CASCADE SAS makes no representation or warranties with respect to the
// performance of this software, and specifically disclaims any responsibility
// for any damages, special or consequential, connected with its use.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * The module which contains Command interface which responses for creating
 * interactive commands, and contains several interactive commands realization. Commands Api based on observables and produce events such as  {@link OccViewer.UserEvents} onTextRequest or
 * onUserMessage.
 * Subscribing to this events allows to customize input of the text from user, customize displaying of commands help message.
 *
 * @example Call sample of interactive command.
 * ```javascript
 *  // Create command executor.
 * const aCommandExecutor = new CommandExecutor(OccViewerModule);
 * ...
 * // Call the sample command
 * aCommandExecutor.call(AddTextLabelCommand);
 * }));
 * ```
 * @example Customize command text input.
 * ```javascript
 * OccViewerModule.eventManager.addEventListener(UserEvents.onTextRequest, ()=>{
 *   let anInitialValue = aCommandExecutor.activeCommand().initialValue;
 *   let aText = prompt( aCommands.control(anUserMessageControl).value(), anInitialValue);
 *   aCommandExecutor.onTextInput(aText);
 *   return 0;
 * });
 * ```
 * @module
 */
import { UserEvents } from "../core/events/events.js";
/**
 * Represents base class for command.
 */
export class Command {
    constructor(theCommandExecutor) {
        this.name = "";
        this._myExecutor = theCommandExecutor;
        this._myModule = this._myExecutor.module();
    }
    /**
     * Cancel will be called when user cancel the command.
     */
    cancel() { }
    /**
     * Start function will be called at start of command.
     */
    start(theParam) { throw new Error("Method is not implemented"); }
    /**
     * Get point helper for commands.
     * It asks user to input the point
     * If command will be canceled callback will never be called
     * @param theCallback the callback
     */
    getPoint(theCallback) {
        let onCancel;
        let onClick = this._myModule.eventManager.createIObservableCallback((theValue) => {
            const aMouseEvent = this._myModule.eventManager.getLastMouseEventInfo();
            if (aMouseEvent.button == 0) {
                const aPoint = this._myModule.pickPoint(aMouseEvent.x, aMouseEvent.y, true);
                if (aPoint) {
                    theCallback(aPoint);
                    this._myModule.eventManager.removeEventListener(UserEvents.onClick, onClick);
                    this._myModule.eventManager.removeEventListener(UserEvents.onCancel, onCancel);
                }
            }
            return 1;
        });
        onCancel = this._myModule.eventManager.createIObservableCallback((theValue) => {
            {
                this._myModule.eventManager.removeEventListener(UserEvents.onCancel, onCancel);
                this._myModule.eventManager.removeEventListener(UserEvents.onClick, onClick);
            }
            return 0;
        });
        this._myModule.eventManager.addEventListener(UserEvents.onCancel, onCancel);
        this._myModule.eventManager.addEventListener(UserEvents.onClick, onClick);
    }
    /**
     * Text input helper.
     * To work correctly should be added ontextrequest listener.
     * If command will be canceled callback will never be called.
     * @param theInitial  initial text value.
     * @param theCallback the callback.
     */
    getText(theInitial, theCallback) {
        return __awaiter(this, void 0, void 0, function* () {
            let onCancel;
            let onText = this._myModule.eventManager.createIObservableCallback((theValue) => {
                theCallback(this._myExecutor.textInput);
                this._myModule.eventManager.removeEventListener(UserEvents.onText, onText);
                this._myModule.eventManager.removeEventListener(UserEvents.onCancel, onCancel);
                return 0;
            });
            onCancel = this._myModule.eventManager.createIObservableCallback((theValue) => {
                this._myModule.eventManager.removeEventListener(UserEvents.onText, onText);
                this._myModule.eventManager.removeEventListener(UserEvents.onCancel, onCancel);
                return 0;
            });
            this._myModule.eventManager.addEventListener(UserEvents.onCancel, onCancel);
            this._myModule.eventManager.addEventListener(UserEvents.onText, onText);
            this.initialValue = theInitial;
            this._myModule.eventManager.emit(UserEvents.onTextRequest, 0);
            return;
        });
    }
}
/**
 * Represents command executor class.
 * It responses for call/cancel commands.
 */
export class CommandExecutor {
    constructor(theModule) {
        /**
         * On key down callback (cancels active command).
         * @param {KeyboardEvent} theEvent the keyboard event
         * @returns void
         */
        this.onKeyDown = (theEvent) => {
            if (!this._myActiveCommand) {
                return;
            }
            var isEscape = false;
            if ("key" in theEvent) {
                isEscape = theEvent.key === "Escape" || theEvent.key === "Esc";
            }
            if (isEscape) {
                this._myModule.eventManager.emit(UserEvents.onCancel, 0);
                this._myActiveCommand.cancel();
            }
        };
        this.textInput = "";
        this._myActiveCommand = null;
        this._myMessage = "";
        this._myModule = theModule;
        window.addEventListener("keydown", this.onKeyDown);
        this.initCommands();
    }
    /**
   * Init commands predefined commands.
   */
    initCommands() {
        this._myModule.eventManager.addEventListener('onEditLengthDimension', this._myModule.eventManager.createIObservableCallback((theVal) => {
            const aLastPickedPoint = this._myModule.eventManager.getLastMouseEventInfo();
            const anId = this._myModule.selectionManager.selectedObjects;
            this.call(EditLengthDimensionCommand, { x: aLastPickedPoint.x, y: aLastPickedPoint.y, id: anId });
            return 0;
        }));
        this._myModule.eventManager.addEventListener('onEditTextLabel', this._myModule.eventManager.createIObservableCallback((theVal) => {
            const aLastPickedPoint = this._myModule.eventManager.getLastMouseEventInfo();
            const anId = this._myModule.selectionManager.selectedObjects;
            this.call(EditTextLabelCommand, { x: aLastPickedPoint.x, y: aLastPickedPoint.y, id: anId });
            return 0;
        }));
    }
    /**
     * Gets user message.
     */
    get message() { return this._myMessage; }
    /**
     * Sets user message.
     */
    set message(theMessage) {
        this._myMessage = theMessage;
        this._myModule.eventManager.emit(UserEvents.onUserMessage, 0);
    }
    /**
     * Create and call command by given type
     * @param theCommand Command
     */
    call(theCommand, theParam) {
        if (this._myActiveCommand) {
            this._myActiveCommand.cancel();
        }
        this._myActiveCommand = new theCommand(this);
        this._myActiveCommand.start(theParam);
    }
    /**
     * Get Viewer.
     * @returns current viewer.
     */
    module() { return this._myModule; }
    /**
   * Set current text input value.
   * @param theValue text input value.
   */
    onTextInput(theValue) {
        this.textInput = theValue;
        this._myModule.eventManager.emit(UserEvents.onText, 0);
    }
    /**
     * Get active command.
     * @returns active command.
     */
    activeCommand() { return this._myActiveCommand; }
}
/**
 * Represents add text label command.
 */
export class AddTextLabelCommand extends Command {
    constructor(theCommandExecutor) {
        super(theCommandExecutor);
        this.name = "Add text label";
    }
    start() {
        // get text
        this._myExecutor.message = "Please input the text";
        this.getText("text input", (theText) => {
            this._myExecutor.message = "Please input the base point of label";
            this.getPoint((thePointPosition) => {
                let aCameraParams = this._myModule.camera.cameraParameters;
                let alineLength = 50;
                let aTextPosition = {
                    x: thePointPosition.x - aCameraParams.direction.x * alineLength,
                    y: thePointPosition.y - aCameraParams.direction.y * alineLength,
                    z: thePointPosition.z - aCameraParams.direction.z * alineLength,
                };
                let aParams = {
                    id: "",
                    text: theText,
                    textSize: 16.0,
                    pointPosition: thePointPosition,
                    textPosition: aTextPosition,
                };
                this._myModule.pmi.addTextLabel(aParams, true);
                this._myModule.eventManager.emit(UserEvents.onSuccess, 0);
            });
        });
    }
    cancel() { }
}
/**
 * Represents edit text label command.
 */
export class EditTextLabelCommand extends Command {
    constructor(theCommandExecutor) {
        super(theCommandExecutor);
        this.name = "Edit text label command";
    }
    /**
     * Start function will be called at start of command.
     * @param {any} theParam the command param
     * @returns void
     */
    start(theParam) {
        this._myExecutor.message = "Please input the text";
        let aParams = this._myModule.pmi.textLabelParams(theParam.id);
        this.getText(aParams.text, (theText) => {
            aParams.text = theText;
            this._myModule.pmi.editTextLabel(aParams, true);
            this._myModule.eventManager.emit(UserEvents.onSuccess, 1);
        });
    }
    cancel() { }
}
/**
 * Represents add line dimension command.
 */
export class AddLengthDimensionCommand extends Command {
    constructor(theCommandExecutor) {
        super(theCommandExecutor);
        this.name = "Add line dimension command";
    }
    start() {
        this._myExecutor.message = "Please input the first point";
        this.getPoint((theStartPoint) => {
            this._myExecutor.message = "Please input the second point";
            this.getPoint((theEndPoint) => {
                let aCameraParams = this._myModule.camera.cameraParameters;
                let aDir = {
                    x: -aCameraParams.direction.x,
                    y: -aCameraParams.direction.y,
                    z: -aCameraParams.direction.z,
                };
                let aParams = {
                    id: "",
                    startPoint: theStartPoint,
                    endPoint: theEndPoint,
                    height: 10,
                    dir: aDir,
                };
                this._myModule.pmi.addLengthDimension(aParams, true);
                this._myModule.eventManager.emit(UserEvents.onSuccess, 1);
            });
        });
    }
    cancel() { }
}
/**
 * Represents edit length dimension command.
 */
export class EditLengthDimensionCommand extends Command {
    constructor(theCommandExecutor) {
        super(theCommandExecutor);
        this.name = "Edit length dimension command";
    }
    /**
     * Start function will be called at start of command.
     * @param {any} theParam the command param
     * @returns void
     */
    start(theParam) {
        this._myExecutor.message = `Edit length dimension ${theParam.x} ${theParam.y} ${theParam.id}`;
    }
    cancel() { }
}
/**
 * Represents add interactive point command.
 */
export class AddInteractivePointCommand extends Command {
    constructor(theCommandExecutor) {
        super(theCommandExecutor);
        this.name = "Add interactive point command";
    }
    /**
     * Start function will be called at start of command.
     * @param {any} theParam the command param
     * @returns void
     */
    start(theParam) {
        this._myExecutor.message = "Please input the first point";
        this.getPoint((theStartPoint) => {
            theParam.point.position = theStartPoint;
            this._myModule.eventManager.emit(UserEvents.onSuccess, 1);
        });
    }
    cancel() { }
}
/**
 * Represents add interactive line command.
 */
export class AddInteractiveLineCommand extends Command {
    constructor(theCommandExecutor) {
        super(theCommandExecutor);
        this.name = "Add interactive line command";
    }
    /**
     * Start function will be called at start of command.
     * @param {any} theParam the command param
     * @returns void
     */
    start(theParam) {
        this._myExecutor.message = "Please input the first point";
        this.getPoint((theStartPoint) => {
            this._myExecutor.message = "Please input the second point";
            this.getPoint((theEndPoint) => {
                theParam.line.pointFrom = theStartPoint;
                theParam.line.pointTo = theEndPoint;
                this._myModule.eventManager.emit(UserEvents.onSuccess, 1);
            });
        });
    }
    cancel() { }
}
